import { useEffect, useRef, useState } from "react";
import { ArrowRight } from "lucide-react";
import CLIPS from "@/components/demo/clips.json";
import M from "@/components/demo/manifest.json";
import { FirmCtx, H, W, rise } from "@/components/demo/DemoKit";
import { SCENES, captionAt } from "@/components/demo/ProductDemo";

export const CLIP_LEN = 15;
export const clipFile = (c) => `/demo/clips/rpopro-${String(c.n).padStart(2, "0")}-${c.key}.mp4`;
const VW = 1080, SW = 1000, S = SW / W;

const EndCard = ({ lt }) => (
  <div className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-[#09090B]" style={rise(lt, 0)}>
    <p className="font-mono text-[28px] uppercase tracking-[0.3em] text-[#D4AF37]">See it live</p>
    <p className="mt-6 font-display text-[84px] font-semibold text-zinc-50">MyNextBigMove.com</p>
    <span className="mt-12 inline-flex items-center gap-3 rounded-full bg-gradient-to-r from-[#F3C649] to-[#D4AF37] px-12 py-6 text-[36px] font-semibold text-black">Book a demo<ArrowRight className="h-9 w-9" /></span>
  </div>
);

// §87 Vertical 1080×1920 social clip for one demo step: /demo-clip?step=radar (recorded by scripts/record_clips.py).
export default function DemoClip() {
  const key = new URLSearchParams(window.location.search).get("step") || "intro";
  const clip = CLIPS.find((c) => c.key === key) || CLIPS[0];
  const sc = M.scenes.find((s) => s.key === clip.key);
  const span = Math.min(CLIP_LEN - 2.5, clip.voice + 1);
  const [t, setT] = useState(0); const t0 = useRef(null);
  useEffect(() => {
    let raf;
    const tick = (now) => { t0.current ??= now; const x = (now - t0.current) / 1000; setT(x); if (x >= CLIP_LEN) window.__clipDone = true; else raf = requestAnimationFrame(tick); };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);
  const Scene = SCENES[clip.key][0];
  const lt = Math.min(t, span) * ((sc.dur - 0.5) / span);
  return (
    <FirmCtx.Provider value="Acme Recruiting">
      <div className="relative overflow-hidden bg-[#09090B]" style={{ width: VW, height: 1920 }} data-testid="demo-clip">
        <div className="absolute -right-60 -top-60 h-[800px] w-[800px] rounded-full bg-[#D4AF37]/15 blur-[160px]" /><div className="absolute -bottom-60 -left-60 h-[700px] w-[700px] rounded-full bg-[#8224e3]/25 blur-[160px]" />
        <div className="absolute left-[40px] right-[40px] top-[90px] flex items-center justify-between"><span className="text-[32px] font-semibold text-zinc-100">MyNextBigMove<span className="text-[#D4AF37]">.com</span></span><span className="rounded-full border border-white/15 px-6 py-2 font-mono text-[24px] text-zinc-400">{clip.n} / {CLIPS.length}</span></div>
        <h1 className="absolute left-[40px] right-[40px] top-[220px] font-display text-[78px] font-semibold leading-[1.05] tracking-tight text-zinc-50" style={rise(t, 0.1)} data-testid="clip-title">{clip.title}</h1>
        <div className="absolute left-[40px] top-[560px] overflow-hidden rounded-[28px] border-2 border-[#D4AF37]/50 shadow-[0_40px_160px_-40px_rgba(212,175,55,0.6)]" style={{ width: SW, height: H * S }}>
          <div className="absolute left-0 top-0 origin-top-left" style={{ width: W, height: H, transform: `scale(${S})` }}><Scene lt={lt} /></div>
        </div>
        <p key={captionAt(clip.text, t, clip.voice)} className="fade-up absolute left-[60px] right-[60px] top-[1300px] text-center text-[46px] font-medium leading-snug text-zinc-50" data-testid="clip-caption">{t < clip.voice + 0.6 ? captionAt(clip.text, t, clip.voice) : ""}</p>
        <div className="absolute bottom-[110px] left-[40px] right-[40px] flex items-center justify-between rounded-full border border-white/10 bg-white/[0.04] px-10 py-6"><span className="text-[30px] text-zinc-300">Sourcing + Revenue Intelligence</span><span className="text-[30px] font-semibold text-[#F3C649]">From $149/mo</span></div>
        {clip.key !== "cta" && t > span && <EndCard lt={t - span} />}
      </div>
    </FirmCtx.Provider>
  );
}
